import './PlanCompareTable.scss'
import { Plan, PricePeriod } from '../../../models/Plan'
import React from 'react'
import { Table } from 'react-bootstrap'
import styled from 'styled-components'

interface PlanCompareTableProps {
  plans: Plan[]
  period: PricePeriod
}

export const PlanCompareTable: React.FC<PlanCompareTableProps> = ({ plans, period }) => {
  const sortedPlans: Plan[] = [...plans]
    .filter((plan) => plan.type !== 'free' && plan.type !== 'payAsYouGo' && !!Object.keys(plan.features).length)
    .sort((a, b) => a.tier - b.tier)

  const categories = sortedPlans
    .map((plan: any) => plan.features.categories || [])
    .flat()
    .reduce((previousValue, currentValue) => {
      if (!Object.keys(previousValue).includes(currentValue.title)) {
        previousValue[currentValue.title] = []
      }
      currentValue.features.forEach((feature: any) => {
        if (!previousValue[currentValue.title].includes(feature.title)) {
          previousValue[currentValue.title].push(feature.title)
        }
      })

      return previousValue
    }, {})

  const parsePriceToYear = (pricePerMonth: any) => {
    const price = parseInt(pricePerMonth.replace('/month', ''))
    if (!isNaN(price)) {
      return `${price * 12}/year`
    }
    return pricePerMonth
  }

  const parsePeriodPrice = (price: any, period: string): string => {
    try {
      const periodPrices = JSON.parse(price)
      if (periodPrices[period] !== undefined) {
        return `$${periodPrices[period]}/${period}`
      }
    } catch (e) {}
    return '-'
  }

  return (
    <Table responsive striped>
      <thead>
        <tr>
          <th></th>
          {sortedPlans.map((plan) => {
            const periodPrice = plan.prices.find((price) => price.period === period)
            if (!periodPrice && plan.type !== 'enterprise') {
              return null
            }
            return (
              <th key={`${plan.id}-header`} className='pricing-table__header'>
                <div className='d-flex flex-column zocks-gap-3 justify-content-between h-100'>
                  <div className='d-flex flex-column'>
                    <h5>{plan.name}</h5>
                  </div>
                </div>
              </th>
            )
          })}
        </tr>
      </thead>
      {Object.entries(categories).map(([title, features]) => {
        return (
          <>
            <div key={`${title}-category-title`} className='lead-medium zocks-mt-3.5 p-3'>
              {title}
            </div>
            <tbody key={`${title}-category-table`}>
              {(features as any).map((feature: any) => {
                return (
                  <tr key={`${title}-${feature}-row`} className='pricing-table__row'>
                    <td className='pricing-table-no-border p-3'>{feature}</td>
                    {sortedPlans.map((plan) => {
                      const periodPrice = plan.prices.find((price) => price.period === period)
                      if (!periodPrice && plan.type !== 'enterprise') {
                        return null
                      }
                      // @ts-ignore
                      const category = plan.features.categories?.find((category) => category.title === title)

                      if (!category) {
                        return (
                          <td key={`${plan.id}-${category}`} className='pricing-table__value-column'>
                            -
                          </td>
                        )
                      }

                      const currentFeature = category.features.find((f: any) => f.title === feature)

                      if (!currentFeature) {
                        return (
                          <td key={`${plan.id}-${category}-${currentFeature}`} className='pricing-table__value-column'>
                            -
                          </td>
                        )
                      }
                      if (typeof currentFeature.value === 'boolean') {
                        return (
                          <td key={`${plan.id}-${category}-${currentFeature}`} className='pricing-table__value-column'>
                            {currentFeature.value ? (
                              <div className='d-flex align-items-center justify-content-center'>
                                <CheckDiv>
                                  <i className='bi bi-check' />
                                </CheckDiv>
                              </div>
                            ) : (
                              <div>-</div>
                            )}
                          </td>
                        )
                      }
                      if (currentFeature.title === 'Number of meetings' && period === 'year' && plan.type !== 'enterprise') {
                        return (
                          <td key={`${plan.id}-${category}-${currentFeature}`} className='pricing-table__value-column'>
                            {parsePriceToYear(currentFeature.value)}
                          </td>
                        )
                      }
                      if (currentFeature.title === 'Cost per additional assistant seat' && plan.type !== 'enterprise') {
                        return (
                          <td key={`${plan.id}-${category}-${currentFeature}`} className='pricing-table__value-column'>
                            {parsePeriodPrice(currentFeature.value, period)}
                          </td>
                        )
                      }
                      return (
                        <td key={`${plan.id}-${category}-${currentFeature}`} className='pricing-table__value-column'>
                          {currentFeature.value}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </>
        )
      })}
    </Table>
  )
}
const CheckDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.25rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background-color: var(--zocks-primary);
`
